/**
 * Host-supplied analytics seam for the studio workbench.
 *
 * The studio is dependency-free of any analytics client — hosts (apps/web,
 * apps/pdf-studio) pass an `onEvent` sink that decides where events go. Events
 * carry ONLY tool metadata (ids, category strings, fixed enums) by design:
 * never file bytes, blobs, File objects, or object URLs, so a host can forward
 * them straight to a product-analytics tool without leaking document contents.
 */

export interface StudioEvent {
  /**
   * - `tool_viewed` — a tool was selected in the rail / home grid.
   * - `tool_run` — a tool produced an output the user downloaded (success).
   * - `tool_file_opened` — the user opened a source document.
   * - `tool_error` — a tool surfaced an error to the user (failed run).
   * - `share_opened` — the share dialog opened, or the user switched its
   *   section (canvas layout). The dialog does not select a tool, so this is
   *   the only event for that step.
   */
  name: 'tool_viewed' | 'tool_run' | 'tool_file_opened' | 'tool_error' | 'share_opened';
  /** Registry tool id (e.g. `merge`), when an active tool is known. */
  tool?: string;
  /** Registry tool category (e.g. `organize`), when an active tool is known. */
  category?: string;
  /**
   * `tool_error` only: a stable machine code for the failure
   * (`StudioApiError.code`, or `unhandled` for anything else).
   */
  code?: string;
  /**
   * `tool_error` only: an authored fixed diagnostic label. The provider never
   * forwards local error text, response messages or arbitrary server codes.
   */
  message?: string;
  /** `share_opened` only: the section the dialog shows. */
  section?: 'link' | 'export';
}

export type StudioEventSink = (event: StudioEvent) => void;

const ERROR_MESSAGES = {
  compute_unavailable: 'Studio compute is unavailable.',
  request_failed: 'Studio request failed.',
  upload_failed: 'Studio upload failed.',
  unauthorized: 'Studio authentication required.',
  forbidden: 'Studio access denied.',
  not_found: 'Studio resource unavailable.',
  bad_request: 'Studio request rejected.',
  internal_error: 'Studio service failed.',
  panel_error: 'Studio panel operation failed.',
  panel_crashed: 'Studio panel failed to render.',
  repair_failed: 'PDF repair failed.',
  edit_text_failed: 'PDF text edit failed.',
  unhandled: 'Studio operation failed.',
} as const;

/** Pure projection at the external event seam; raw UI causes stay local. */
export function studioErrorMetadata(code?: string): Pick<StudioEvent, 'code' | 'message'> {
  const safeCode =
    code && Object.hasOwn(ERROR_MESSAGES, code)
      ? (code as keyof typeof ERROR_MESSAGES)
      : 'unhandled';
  return { code: safeCode, message: ERROR_MESSAGES[safeCode] };
}
